import { formatDate } from "@its-engineering-and-development/ui-foundation/utils";
import type { DashboardDateRange } from "./dashboard-types";

/** Parses an ISO date key (YYYY-MM-DD) as a local calendar date. */
export function parseLocalDate(dateKey: string): Date {
    const [year, month, day] = dateKey.split("-").map(Number);
    return new Date(year!, month! - 1, day);
}

/** Formats a local date as an ISO date key (YYYY-MM-DD). */
export function toLocalDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

/** Inclusive day count between two ISO date keys. */
export function getInclusiveDayCount(startDate: string, endDate: string): number {
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    return Math.max(1, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1);
}

/** Ensures start <= end and recomputes the inclusive day span. */
export function normalizeDashboardDateRange(range: DashboardDateRange): DashboardDateRange {
    let { startDate, endDate } = range;

    if (parseLocalDate(startDate) > parseLocalDate(endDate)) {
        [startDate, endDate] = [endDate, startDate];
    }

    return {
        ...range,
        startDate,
        endDate,
        days: getInclusiveDayCount(startDate, endDate),
    };
}

export function createDateRangeFromToday(days: number, preset: DashboardDateRange["preset"]): DashboardDateRange {
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - (days - 1));

    return normalizeDashboardDateRange({
        preset,
        days,
        startDate: toLocalDateKey(start),
        endDate: toLocalDateKey(end),
    });
}

export function getDateRangeButtonLabel(range: DashboardDateRange): string {
    if (range.preset !== "Custom") {
        return range.preset;
    }

    const start = formatDate(range.startDate, "MMM d, yyyy");
    const end = formatDate(range.endDate, "MMM d, yyyy");
    return `${start} – ${end}`;
}

export function createDefaultDateRange(days: number, preset: DashboardDateRange["preset"] = "30 Days"): DashboardDateRange {
    return createDateRangeFromToday(days, preset);
}
